import { noop } from 'lodash-es';
import React, { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '../common/Dialog';
import { createGallery, CreateGalleryOptions, updateGallery } from '../services/GalleryService';
import {
  CurrentFolder,
  Folder,
  Gallery,
  PanoramaDetails,
  RootFolder,
} from '../services/UploadManagementServiceTypes';
import { CreateGalleryModal } from './CreateGalleryModal';
import { InvalidSaveModal } from './InvalidSaveModal';
import { ReplaceGalleryModal } from './ReplaceGalleryModal';
import { TourModeModal, WaypointOptions } from './TourModeModal';

interface ActionBase<T extends string> {
  action: T;
}

export interface CreateAction extends ActionBase<'create'> {
  galleryName: string;
  destinationGuid: string | null;
}

export interface UpdateAction extends ActionBase<'update'> {
  galleryName: string;
  destinationGuid: string | null;
  duplicateId: string;
}

export type GalleryActions = CreateAction | UpdateAction;

export type ViewState = 'save' | 'replace' | 'invalid' | 'waypoints' | 'waypoints2' | null;

interface UseSaveGalleryProps {
  panoramaDetails: PanoramaDetails[];
  onSaveSuccess?: (gallery: Gallery) => void;
  currentGallery?: Gallery;
  currentFolder?: CurrentFolder;
  waypointsShown: WaypointOptions;
}

export const useSaveGallery = ({
  currentFolder,
  panoramaDetails,
  onSaveSuccess = noop,
  currentGallery,
  waypointsShown,
}: UseSaveGalleryProps) => {
  const [view, setView] = useState<ViewState>(null);
  const [tourMode, setTourMode] = useState<WaypointOptions>(waypointsShown);

  const createMutation = useMutation(createGallery, {
    onSuccess: onSaveSuccess,
    retry: false,
  });

  const updateMutation = useMutation(updateGallery, {
    onSuccess: onSaveSuccess,
    retry: false,
  });

  const galleryRef = useRef<GalleryActions | null>(null);
  const isAllowedToSave = panoramaDetails.length > 1;
  const orderedPanoramas = panoramaDetails.map((p, index) => ({ guid: p.panoramaGuid, index }));
  const mutateGalleryAsync = (args: CreateGalleryOptions) =>
    createMutation.mutateAsync({
      ...args,
      tourMode: args.tourMode,
    });

  const onClose = () => setView(null);

  const onCreate = async (action: GalleryActions): Promise<void> => {
    if (action.action === 'create') {
      await mutateGalleryAsync({
        name: action.galleryName,
        parentGuid: action.destinationGuid,
        referencePanoramas: orderedPanoramas,
        tourMode,
      });
      onClose();
    }
    if (action.action === 'update') {
      galleryRef.current = action;
      setView('replace');
    }
  };

  const onReplace = async () => {
    if (galleryRef.current?.action === 'update') {
      const { duplicateId } = galleryRef.current;
      await updateMutation.mutateAsync({
        referencePanoramas: orderedPanoramas,
        tourMode,
        guid: duplicateId,
      });
      onClose();
    }
  };

  const onKeepBoth = async () => {
    if (galleryRef.current?.action === 'update') {
      const timeStamp = new Date(Date.now()).toISOString();
      const result = await mutateGalleryAsync({
        name: `${galleryRef.current.galleryName} ${timeStamp}`,
        parentGuid: galleryRef.current.destinationGuid,
        referencePanoramas: orderedPanoramas,
        tourMode,
      });
      onClose();
      onSaveSuccess(result);
    }
  };

  const onSaveAs = () => setView('waypoints');

  const onSave = async () => {
    if (!isAllowedToSave) {
      return setView('invalid');
    }
    if (currentGallery && !waypointsShown && currentFolder) {
      return setView('waypoints2');
    }

    if (!currentGallery && waypointsShown) {
      return setView('save');
    }

    if (currentGallery && waypointsShown) {
      const { guid } = currentGallery;
      return updateMutation.mutate({
        referencePanoramas: orderedPanoramas,
        tourMode: waypointsShown,
        guid,
      });
    }
    onSaveAs();
  };

  const onConfirmWaypoints = async (option: WaypointOptions) => {
    if (view === 'waypoints2' && currentGallery && currentFolder) {
      const { name } = currentGallery;
      const { guid: parentGuid } = currentFolder;
      await mutateGalleryAsync({
        name,
        parentGuid,
        referencePanoramas: orderedPanoramas,
        tourMode: option,
      });
      return onClose();
    }
    setView('save');
    setTourMode(option);
  };

  return {
    onSave,
    isLoading: createMutation.isLoading || updateMutation.isLoading,
    view,
    onSaveAs,
    onCreate,
    onReplace,
    onKeepBoth,
    onClose,
    onConfirmWaypoints,
  };
};

const waypointsDescription =
  'Set the default navigation mode for the viewer of the linked panoramas. You can change it afterwards.';

export type SaveGalleryModalsProps = {
  currentFolder: Folder | RootFolder;
  waypointsShown: WaypointOptions;
} & ReturnType<typeof useSaveGallery>;

export const SaveGalleryModals: React.FC<SaveGalleryModalsProps> = ({
  view,
  onKeepBoth,
  onClose,
  onReplace,
  onCreate,
  currentFolder,
  onConfirmWaypoints,
  waypointsShown,
}) => (
  <>
    <Dialog open={view === 'save'}>
      <CreateGalleryModal
        onAction={onCreate}
        onClose={onClose}
        onContinue={onClose}
        currentFolder={currentFolder}
      />
    </Dialog>
    <Dialog open={view === 'replace'}>
      <ReplaceGalleryModal onReplace={onReplace} onKeep={onKeepBoth} onClose={onClose} />
    </Dialog>
    <Dialog open={view === 'invalid'}>
      <InvalidSaveModal onClose={onClose} />
    </Dialog>
    <Dialog open={view === 'waypoints' || view === 'waypoints2'}>
      <TourModeModal
        initialConfig={waypointsShown}
        onClose={onClose}
        onConfirm={onConfirmWaypoints}
        cancelButtonLabel={'Continue Editing'}
        confirmButtonLabel={'Next'}
        description={waypointsDescription}
      />
    </Dialog>
  </>
);
